import React, { useState } from 'react';

import { FilterIcon, SettingsIcon } from '@vlabs/icons';
import { Badge } from '@vlabs/uikit';
import { Button } from '@vlabs/uikit/components/controls/button';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import { Switch } from '@vlabs/uikit/components/controls/switch/Switch';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { selectFacecoverVersion } from '@vlapps/point/src/app/store/selectors';

import st from './Settings.module.sass';
import { useWorkspaceFilters } from '../filters/useWorkspaceFilters';
import { VIDEO_ANALYTICS_FILTERS } from '../filters/videoAnalyticsFilters';
import {
  getDetectionAndMatchFilterActivity,
  getVideoAnalyticsFilterActivity,
} from '../filters/workspaceFilterActivity';
import {
  connectAsyncEvents,
  disconnectAsyncEvents,
} from '../store.js';

export const EVENT_ROLL_SIDEBARS = {
  FILTERS: 'filters',
  SETTINGS: 'settings',
} as const;

export type SidebarName = typeof EVENT_ROLL_SIDEBARS[keyof typeof EVENT_ROLL_SIDEBARS];

interface SettingsProps {
  activeSidebar: SidebarName | null;
  onToggleSidebar: (name: SidebarName) => void;
}

export function Settings({
  activeSidebar,
  onToggleSidebar,
}: SettingsProps): React.JSX.Element {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const connectWs = () => dispatch(connectAsyncEvents());

  const { filters } = useWorkspaceFilters();
  const facecoverVersion = useSelector(selectFacecoverVersion);
  const detectionActivity = getDetectionAndMatchFilterActivity(filters);

  const [isWsConnect, setIsWsConnect] = useState(true);

  const onSwitchWsConnection = () => {
    if (isWsConnect) {
      disconnectAsyncEvents();
      setIsWsConnect(false);
    } else {
      connectWs();
      setIsWsConnect(true);
    }
  };

  const countVAFilters = VIDEO_ANALYTICS_FILTERS.reduce((count, data) => count
    + getVideoAnalyticsFilterActivity(filters, {
      analyticType: data.analyticType,
      facecoverVersion,
      scoreField: data.scoreField,
      scoreFields: data.scoreFields,
    }).totalCount, 0);
  const countActiveFilters = detectionActivity.totalCount + countVAFilters;

  return (
    <div className={st.Root}>
      <Switch
        data-testid="workspace.getAllEventsSwitch"
        id="ws"
        label={t('lunapoint:get_events')}
        name="ws"
        onChange={onSwitchWsConnection}
        value={isWsConnect}
      />

      <div className={st.Root_Buttons}>
        <Button
          data-testid="workspace.openFilters"
          icon={<FilterIcon />}
          kind={activeSidebar === EVENT_ROLL_SIDEBARS.FILTERS ? 'secondary' : 'primary'}
          onClick={() => onToggleSidebar(EVENT_ROLL_SIDEBARS.FILTERS)}
          size="small"
          variant={activeSidebar === EVENT_ROLL_SIDEBARS.FILTERS ? 'filled' : 'outlined'}
        >
          {t('filters_title')}
          {(countActiveFilters > 0) && (
            <Badge
              className={st.FiltersQuantityBadge}
              kind="negative"
              shape="round"
              value={countActiveFilters}
            />
          )}
        </Button>
        <IconButton
          data-testid="workspace.openSettings"
          icon={<SettingsIcon />}
          kind="secondary"
          onClick={() => onToggleSidebar(EVENT_ROLL_SIDEBARS.SETTINGS)}
          shape="square"
          size="small"
          variant={activeSidebar === EVENT_ROLL_SIDEBARS.SETTINGS ? 'filled' : 'outlined'}
        />
      </div>
    </div>
  );
}
